// src/components/templates/Rejected.tsx
import React, { useState } from 'react';
import { UserDefinedTemplate } from '../../features/templates/types';
import { FiSearch } from 'react-icons/fi';
import profile from '../../assets/profile.png';
import CreateTemplateModal from './CreateTemplateModal';

interface Props {
  templates: UserDefinedTemplate[];
  onSelect?: (id: string) => void;
  role: string;
}

const ITEMS_PER_PAGE = 9;

const FiSearchComponent = FiSearch as any;

const Rejected: React.FC<Props> = ({ templates, role }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);

  // Filter only rejected templates
  const rejectedTemplates = templates.filter((t) => t.status === 'REJECTED');

  // Get unique categories from rejected templates
  const categories = Array.from(
    new Set(
      rejectedTemplates.map((t) => t.category).filter(Boolean) as string[]
    )
  );

  const handleCategoryChange = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
    setCurrentPage(1); // Reset page on filter change
  };

  // Apply both search and category filters
  const filteredTemplates = rejectedTemplates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description || '')
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategories.length === 0 ||
      (template.category && selectedCategories.includes(template.category));

    return matchesSearch && matchesCategory;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTemplates = filteredTemplates.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleView = (template: UserDefinedTemplate) => {
    const html = template.html || '';
    const css = template.css || '';

    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow pop-ups to view the template preview.');
      return;
    }

    win.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>View: ${template.name}</title>
        <style>
          ${css}
          body { margin: 0; padding: 0; background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .preview-container { max-width: 1200px; margin: 0 auto; padding: 20px; }
          .preview-card { background: white; border-radius: 16px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); overflow: hidden; margin: 40px 0; }
          .preview-header { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); color: white; padding: 24px 32px; }
          .preview-title { margin: 0; font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
          .preview-meta { display: flex; gap: 12px; margin-top: 16px; flex-wrap: wrap; }
          .preview-badge { background: rgba(255,255,255,0.2); color: white; padding: 6px 16px; border-radius: 20px; font-size: 14px; font-weight: 500; backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.3); }
          .preview-description { background: rgba(255,255,255,0.1); padding: 16px; border-radius: 12px; margin-top: 16px; font-size: 15px; line-height: 1.5; border: 1px solid rgba(255,255,255,0.2); }
          .template-content { padding: 40px; background: white; min-height: 400px; }
          .read-only-notice { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 12px 20px; border-radius: 8px; margin: 20px 32px 0; font-size: 14px; display: flex; align-items: center; gap: 8px; }
          .read-only-notice::before { content: "Rejected"; font-size: 18px; font-weight: 600; }
          .preview-footer { background: #fef2f2; padding: 20px 32px; border-top: 1px solid #fecaca; display: flex; justify-content: space-between; align-items: center; color: #dc2626; font-size: 14px; }
          @media (max-width: 768px) { .preview-header { padding: 20px; } .preview-title { font-size: 24px; } .template-content { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="preview-container">
          <div class="preview-card">
            <div class="preview-header">
              <h1 class="preview-title">${template.name}</h1>
              <div class="preview-meta">
                <span class="preview-badge">Rejected</span>
                <span class="preview-badge">${
                  template.category || 'Uncategorized'
                }</span>
               
                <span class="preview-badge">Date: ${new Date(
                  template.createdAt
                ).toLocaleDateString()}</span>
              </div>
              ${
                template.description
                  ? `<div class="preview-description">${template.description}</div>`
                  : ''
              }
            </div>
            <div class="read-only-notice">
              This template has been rejected and is not available for use.
            </div>
            <div class="template-content">
              ${
                html ||
                '<div style="text-align: center; padding: 60px 20px; color: #94a3b8;"><p>No content available</p></div>'
              }
            </div>
            <div class="preview-footer">
              <div>Template ID: ${template.id}</div>
              <span>Preview generated on ${new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <script>
          document.addEventListener('keydown', (e) => { if (e.key === 'Escape') window.close(); });
          window.focus();
        </script>
      </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <div className='flex gap-6'>
      {/* Sidebar */}
      <div
        className='w-1/5 bg-white rounded-md p-5 border border-gray-200 shadow-sm sticky top-6'
        style={{ height: 'fit-content' }}
      >
        <button
          className='w-full text-left font-medium mb-4 px-3 py-2 rounded flex justify-between items-center'
          style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
        >
          Rejected Templates
          <span className='bg-red-600 text-white text-xs px-2 py-1 rounded'>
            {rejectedTemplates.length}
          </span>
        </button>

        <div className='space-y-2'>
          <div className='text-sm text-gray-500 mb-2'>Filter by category:</div>
          {categories.length > 0 ? (
            categories.map((cat) => {
              const count = rejectedTemplates.filter(
                (t) => t.category === cat
              ).length;
              return (
                <label
                  key={cat}
                  className='flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer'
                >
                  <input
                    type='checkbox'
                    checked={selectedCategories.includes(cat)}
                    onChange={() => handleCategoryChange(cat)}
                    className='w-4 h-4 text-red-600 rounded focus:ring-red-500'
                  />
                  <span className='text-sm text-gray-700 flex-1'>{cat}</span>
                  <span className='text-xs text-gray-500'>({count})</span>
                </label>
              );
            })
          ) : (
            <div className='text-gray-400 text-sm italic'>
              No categories available
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1'>
        <div className='flex justify-between items-center mb-6'>
          <div className='relative flex-1 w-full'>
            <input
              type='text'
              placeholder='Search rejected templates...'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset page on search
              }}
              className='w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500'
            />
            <FiSearchComponent
              className='absolute right-3 top-3 text-gray-400'
              size={20}
            />
          </div>
          {role !== 'admin' && (
            <button
              className='ml-4 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm transition'
              onClick={() => setShowModal(true)}
            >
              + Create Template
            </button>
          )}
        </div>

        {filteredTemplates.length > 0 ? (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {paginatedTemplates.map((template) => (
                <div
                  key={template.id}
                  className='group relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow'
                >
                  <div className='aspect-[4/3] bg-gray-100 relative overflow-hidden'>
                    <img
                      src={
                        template.thumbnail ||
                        'https://placehold.co/400x300?text=Template'
                      }
                      alt={template.name}
                      className='w-full h-full object-cover'
                      onError={(e: any) =>
                        (e.currentTarget.src =
                          'https://placehold.co/400x300?text=No+Preview')
                      }
                    />

                    <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center opacity-0 group-hover:opacity-100'>
                      <div className='flex gap-3 transform translate-y-4 group-hover:translate-y-0 transition-transform'>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(template);
                          }}
                          className='px-5 py-2.5 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 shadow-lg flex items-center gap-2'
                        >
                          <svg
                            className='w-4 h-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='2'
                              d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                            />
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='2'
                              d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                            />
                          </svg>
                          View
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='p-4'>
                    <div className='flex justify-between items-start mb-2'>
                      <h3 className='font-semibold text-gray-800 truncate pr-2'>
                        {template.name}
                      </h3>
                      <span className='bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full'>
                        REJECTED
                      </span>
                    </div>
                    <p className='text-sm text-gray-600 line-clamp-2'>
                      {template.description || 'No description'}
                    </p>
                    <div className='flex justify-between items-center text-xs text-gray-500 mt-3'>
                      <span>
                        Category: {template.category || 'Uncategorized'}
                      </span>
                      <div className='flex items-center gap-1'>
                        <img
                          src={profile}
                          alt='reviewer'
                          className='w-5 h-5 rounded-full'
                        />
                        <span className='font-medium text-gray-700'>
                          Reviewer
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Hover Overlay with View Button */}
                  {/* <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center opacity-0 group-hover:opacity-100'>
                    <div className='flex gap-3 transform translate-y-4 group-hover:translate-y-0 transition-transform'>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(template);
                        }}
                        className='px-5 py-2.5 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 shadow-lg flex items-center gap-2'
                      >
                        <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                        View
                      </button>
                    </div>
                  </div> */}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className='mt-10 flex items-center justify-center gap-2'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${
                        currentPage === page
                          ? 'bg-orange-500 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            )}

            <div className='mt-4 text-center text-sm text-gray-600'>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredTemplates.length)}{' '}
              of {filteredTemplates.length} templates
            </div>
          </>
        ) : (
          <div className='text-center py-16'>
            <p className='text-gray-500 text-lg'>No rejected templates found</p>
            <p className='text-gray-400 mt-2'>
              {searchQuery || selectedCategories.length > 0
                ? 'Try adjusting your search or category filters'
                : 'No templates have been rejected yet.'}
            </p>
          </div>
        )}
      </div>

      <CreateTemplateModal
        fromTab2={'yourTemplates'}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default Rejected;
